// The console for the person who sends the driver.
//
// The shape of this file is the product. There is one screen at rung 0 —
// an address, who is driving, send — and everything else in this app is
// mounted **only** when `unlockedRungs` says the work has earned it. Not
// hidden behind a toggle: absent. A restaurant that never grows past one
// rider will never see a roster, a board, a route, an offer or a seed,
// and will never be told they exist.

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  createWayfindSession,
  THREAD_STATE,
  type Offer,
  type ProfileId,
  type WayfindSession
} from "@wayfind/core";
import markUrl from "@wayfind/brand/assets/mark.svg";
import { config, clockShiftMillis, nowMillis, nowSeconds } from "./config";
import { useStore } from "./useStore";
import { createDispatcher, type Dispatcher } from "./ops/dispatch";
import { dispatchBundle, toHex } from "./ops/engine";
import { acceptOffer, dismissOffer, has, ladderView } from "./ops/ladder";
import {
  emptyState,
  loadState,
  saveState,
  storageWritable,
  type FleetState,
  type StoredDevice,
  type StoredJob,
  type StoredOffer,
  type StoredRoute,
  type StoredRouteDay,
  type StoredSeed
} from "./ops/state";
import { useJobWatch, watchable } from "./ops/watch";
import { vocabularyFor, type Vocabulary } from "./ops/words";
import { Copyable, Note, Panel } from "./components/Bits";
import { Books } from "./components/Books";
import { Compose } from "./components/Compose";
import { DayBoard } from "./components/DayBoard";
import { Handover } from "./components/Handover";
import { History } from "./components/History";
import { JobDetail } from "./components/JobDetail";
import { Live } from "./components/Live";
import { LadderEvidence, OfferBanner } from "./components/Ladder";
import { Offers } from "./components/Offers";
import { Routes } from "./components/Routes";
import { Seed } from "./components/Seed";
import { ProfileSwitch, Setup } from "./components/Setup";
import { PairPhone, Roster } from "./components/Drivers";

type Theme = "system" | "light" | "dark";

/** What the middle of the screen is doing. Never more than one thing. */
type Focus =
  | { kind: "compose" }
  | { kind: "sent"; job: StoredJob }
  | { kind: "pair"; forOfferId: string | null }
  | { kind: "handover"; job: StoredJob };

/**
 * Which room of the console the operator is in.
 *
 * The ladder still decides what *exists* — a takeaway with one rider is
 * never shown routes, offers or a seed. What it never governed, and what
 * this adds, is being able to put down the composer and go and look at
 * the day: tracking and the record are not rung concepts, they are the
 * two questions the job of dispatching is made of.
 */
type View = "send" | "live" | "history" | "people" | "settings";

export function App(): JSX.Element {
  const [session, setSession] = useState<WayfindSession | null>(null);
  const [openError, setOpenError] = useState<string | null>(null);
  const [state, setState] = useState<FleetState>(() => loadState());
  const [focus, setFocus] = useState<Focus>({ kind: "compose" });
  const [view, setView] = useState<View>("send");
  // The job the composer just produced, so its handover sheet is shown
  // once and the record is shown every time after.
  const [justSent, setJustSent] = useState<string | null>(null);
  const [theme, setTheme] = useState<Theme>("system");
  const started = useRef(false);
  const writable = useRef(storageWritable());

  const sessionState = useStore(session?.store ?? null);

  useEffect(() => {
    if (theme === "system") document.documentElement.removeAttribute("data-theme");
    else document.documentElement.setAttribute("data-theme", theme);
  }, [theme]);

  // One session per tab, guarded by a ref rather than by the effect's own
  // cleanup: StrictMode runs this twice in development and a teardown
  // would leave the second early return holding nothing.
  useEffect(() => {
    if (started.current) return;
    started.current = true;
    createWayfindSession({
      engineBaseUrl: config.engineBaseUrl,
      routeGraphUrl: config.routeGraphUrl || null,
      datasetId: config.datasetId || null,
      engineMode: config.routeProfile,
      // A console that mints links and cannot open one is a dispatcher
      // who has to phone the driver to find out whether the parcel
      // arrived. The addresses here are locations, never capabilities:
      // the fleet's own seed when one is enrolled (rung 5 — the operator
      // said they run their own machine), and the configured keeper
      // otherwise. Without either, the session stays on loopback and the
      // board honestly reports hearing nothing.
      bootstrap: [
        ...(loadState().seed?.addresses ?? []),
        ...config.meshBootstrap
      ].filter((address, index, all) => all.indexOf(address) === index),
      id: "console"
    })
      .then(setSession)
      .catch((error: unknown) => setOpenError(error instanceof Error ? error.message : String(error)));
  }, []);

  const update = useCallback((mutate: (current: FleetState) => FleetState) => {
    setState(current => {
      const next = mutate(current);
      saveState(next);
      return next;
    });
  }, []);

  // The dispatcher's own keys, made once and kept here. The issuer seed
  // signs jobs and offers; the console's device key is what lets this
  // machine open what it sent — cancel it, hand it on, read a photo back.
  useEffect(() => {
    if (!session || state.issuerSeedHex) return;
    const threads = dispatchBundle(session.bundles);
    let live = true;
    void (async () => {
      const issuer = await threads.generateThreadKeypair();
      const device = await threads.generateDeviceKeypair();
      if (!live) return;
      update(current => current.issuerSeedHex ? current : {
        ...current,
        issuerSeedHex: toHex(issuer.privateSeed),
        consoleDevice: {
          privateKeyHex: toHex(device.privateKey),
          publicKeyHex: toHex(device.publicKey),
          name: "This console"
        }
      });
    })();
    return () => { live = false; };
  }, [session, state.issuerSeedHex, update]);

  const dispatcher: Dispatcher | null = useMemo(
    () => (session ? createDispatcher(session) : null),
    [session]
  );
  const threads = useMemo(() => (session ? dispatchBundle(session.bundles) : null), [session]);

  const now = nowSeconds();
  const ladder = useMemo(() => ladderView(state, now), [state, now]);
  const vocabulary = useMemo(() => vocabularyFor(state.profile ?? "courier"), [state.profile]);

  // Every job still open, followed on the mesh. A finished one is let go:
  // its records are settled, and holding the subscription would spend a
  // phone's battery relaying a round that ended this morning.
  const focusedJobId = focus.kind === "sent" || focus.kind === "handover" ? focus.job.id : null;
  const watching = useMemo(
    () => state.jobs
      .filter(job => {
        if (!watchable(job, now)) return false;
        // Whatever is on screen is followed even once it has settled: an
        // operator opening a finished round wants what the run said, and
        // dropping the subscription the moment it completed would answer
        // "nothing has been published", which is the opposite of true.
        if (job.id === focusedJobId) return true;
        return job.finishedUnixSeconds == null;
      })
      .map(job => ({
        id: job.id,
        trackUrl: job.trackUrl,
        stops: job.stops,
        notAfter: job.notAfter
      })),
    // `now` deliberately absent: it ticks every render and would rebuild
    // the watch list — and with it every subscription — once a second.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [state.jobs, focusedJobId]
  );
  const watch = useJobWatch(session, watching);
  const live = watch.snapshots;

  // What the driver said, written down. The run's own signed record is
  // what settles a job — not this console's opinion of it — so a round
  // whose last stop has an outcome closes itself, and the operator stops
  // having to remember to press Done. A job nobody is publishing about is
  // untouched: silence is not completion.
  useEffect(() => {
    const settled: string[] = [];
    for (const [id, snapshot] of live) {
      const finished = snapshot.status.state === THREAD_STATE.COMPLETED
        || snapshot.status.state === THREAD_STATE.CANCELED;
      if (finished) settled.push(id);
    }
    if (!settled.length) return;
    update(current => {
      const open = new Set(
        current.jobs.filter(job => job.finishedUnixSeconds == null).map(job => job.id)
      );
      const closing = settled.filter(id => open.has(id));
      if (!closing.length) return current;
      const at = nowSeconds();
      return {
        ...current,
        jobs: current.jobs.map(job => (closing.includes(job.id)
          ? { ...job, finishedUnixSeconds: job.finishedUnixSeconds ?? at }
          : job))
      };
    });
  }, [live, update]);

  // The first record heard is when the run actually began, which is not
  // when it was sealed: a job cut at 08:00 and picked up at 09:40 was
  // stood still for an hour and the board should not claim otherwise.
  useEffect(() => {
    const started: string[] = [];
    for (const [id, snapshot] of live) {
      if (snapshot.status.observed.ever) started.push(id);
    }
    if (!started.length) return;
    update(current => {
      const unstarted = new Set(
        current.jobs.filter(job => job.startedUnixSeconds == null).map(job => job.id)
      );
      const marking = started.filter(id => unstarted.has(id));
      if (!marking.length) return current;
      const at = nowSeconds();
      return {
        ...current,
        jobs: current.jobs.map(job => (marking.includes(job.id)
          ? {
            ...job,
            startedUnixSeconds: live.get(job.id)?.status.observed.atUnixSeconds ?? at
          }
          : job))
      };
    });
  }, [live, update]);

  // A dispatcher types an address and expects to keep typing. "/" puts
  // the cursor back in the field from anywhere that is not already a
  // field, and Escape backs out of whatever panel is open.
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;
      const typing = target?.tagName === "INPUT" || target?.tagName === "TEXTAREA" || target?.isContentEditable;
      if (event.key === "Escape" && focus.kind !== "compose") {
        setFocus({ kind: "compose" });
        return;
      }
      if (event.key === "/" && !typing) {
        const field = document.querySelector<HTMLInputElement>("input[role=combobox]");
        if (field) { event.preventDefault(); field.focus(); }
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [focus.kind]);

  const onOffer = useCallback((offer: Offer, take: boolean) => {
    update(current => (take ? acceptOffer(current, offer, nowSeconds()) : dismissOffer(current, offer, nowSeconds())));
  }, [update]);

  const enrol = useCallback((device: StoredDevice, forOfferId: string | null) => {
    update(current => ({
      ...current,
      devices: [...current.devices, device],
      offers: forOfferId
        ? current.offers.map(offer => (offer.id === forOfferId
          ? { ...offer, bidDeviceIds: [...offer.bidDeviceIds, device.id] }
          : offer))
        : current.offers
    }));
    setFocus({ kind: "compose" });
  }, [update]);

  // Today, for the live room. A day rather than a rolling 24 hours would
  // be truer to how an operator thinks, and is a change with a timezone
  // in it; this stays as the board has always counted.
  const todaysJobs = useMemo(
    () => state.jobs.filter(job => now - job.createdUnixSeconds < 86400),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [state.jobs]
  );

  /**
   * A job opened from anywhere — the live room, the history, the board —
   * gets the full record rather than the handover sheet. The handover
   * sheet is what you want in the ninety seconds after sending; the
   * record is what you want for the rest of the job's life, and an
   * operator clicking a delivery from last Tuesday is asking the second
   * question.
   */
  const openJob = useCallback((job: StoredJob) => {
    setFocus({ kind: "sent", job });
  }, []);

  const finishJob = useCallback((job: StoredJob) => {
    update(current => ({
      ...current,
      jobs: current.jobs.map(entry => (entry.id === job.id
        ? { ...entry, finishedUnixSeconds: nowSeconds() }
        : entry))
    }));
  }, [update]);

  if (!state.profile) {
    return (
      <Shell theme={theme} setTheme={setTheme} profile={null} onProfile={() => {}}>
        <Setup onPick={(profile: ProfileId) => update(current => ({ ...emptyState(), ...current, profile }))} />
      </Shell>
    );
  }

  const showRoster = has(ladder, 2);
  const showBoard = has(ladder, 2);
  const showRoutes = has(ladder, 3);
  const showOffers = has(ladder, 4);
  const showSeed = has(ladder, 5);
  // The books are not a rung. Backing up an operation is not a capability
  // the work has to earn — the first evening's jobs are the ones a
  // cleared browser loses, and the issuer seed is minted before the first
  // job goes out. It appears as soon as there is anything to lose.
  const showBooks = state.jobs.length > 0 || state.devices.length > 0 || state.routes.length > 0;
  const rail = showRoster || showRoutes || showOffers || showSeed || showBoard || showBooks;


  // A job held in `focus` is a snapshot from when it was clicked, and the
  // record moves underneath it — a mark lands, the run closes. Read it
  // back out of state so an open detail screen is never stale.
  const focusedJob = focus.kind === "sent" || focus.kind === "handover"
    ? state.jobs.find(job => job.id === focus.job.id) ?? focus.job
    : null;

  const middle = (() => {
    if (focus.kind === "pair") {
      return (
        <PairPhone
          threads={threads}
          channel={session?.channel ?? null}
          state={state}
          vocabulary={vocabulary}
          onEnrol={device => enrol(device, focus.forOfferId)}
          onClose={() => setFocus({ kind: "compose" })}
        />
      );
    }
    if (focus.kind === "handover" && focusedJob) {
      return (
        <HandOverTo
          job={focusedJob}
          state={state}
          dispatcher={dispatcher}
          onCancel={() => setFocus({ kind: "compose" })}
          onDone={(job, deviceId, driverUrl, sealedB64) => {
            update(current => ({
              ...current,
              jobs: current.jobs.map(entry => (entry.id === job.id
                ? {
                  ...entry,
                  deviceId,
                  heldBy: deviceId,
                  driverUrl,
                  sealedB64,
                  usedRungs: entry.usedRungs.includes(2) ? entry.usedRungs : [...entry.usedRungs, 2]
                }
                : entry))
            }));
            const next = { ...job, deviceId, heldBy: deviceId, driverUrl, sealedB64 };
            setFocus({ kind: "sent", job: next });
          }}
        />
      );
    }
    if (focus.kind === "sent" && focusedJob) {
      // Straight off the composer this is a handover: the operator has a
      // driver in front of them and a code to show. Everywhere else it is
      // the record.
      return justSent === focusedJob.id ? (
        <Handover
          job={focusedJob}
          vocabulary={vocabulary}
          sealedToName={
            state.devices.find(device => device.id === focusedJob.deviceId)?.name ?? null
          }
          live={live.get(focusedJob.id) ?? null}
          proof={watch.proofOf(focusedJob.id)}
          state={state}
          dispatcher={dispatcher}
          onDone={() => { setJustSent(null); setFocus({ kind: "compose" }); }}
        />
      ) : (
        <JobDetail
          job={focusedJob}
          snapshot={live.get(focusedJob.id) ?? null}
          proof={watch.proofOf(focusedJob.id)}
          state={state}
          dispatcher={dispatcher}
          engine={session?.engine ?? null}
          vocabulary={vocabulary}
          onBack={() => setFocus({ kind: "compose" })}
          onHandOver={job => setFocus({ kind: "handover", job })}
          onFinish={job => { finishJob(job); setFocus({ kind: "compose" }); }}
        />
      );
    }

    switch (view) {
      case "live":
        return (
          <Live
            state={state}
            jobs={todaysJobs}
            live={live}
            engine={session?.engine ?? null}
            vocabulary={vocabulary}
            onOpen={openJob}
          />
        );
      case "history":
        return (
          <History
            state={state}
            live={live}
            vocabulary={vocabulary}
            onOpen={openJob}
          />
        );
      case "people":
        return (
          <Roster
            state={state}
            vocabulary={vocabulary}
            onPair={() => setFocus({ kind: "pair", forOfferId: null })}
            onRename={(id, name) => update(current => ({
              ...current,
              devices: current.devices.map(device => (device.id === id ? { ...device, name } : device))
            }))}
            onRetire={id => update(current => ({
              ...current,
              devices: current.devices.map(device => (device.id === id
                ? { ...device, retiredUnixSeconds: nowSeconds() }
                : device))
            }))}
          />
        );
      case "settings":
        return (
          <div className="stack">
            {showBooks ? (
              <Books
                state={state}
                vocabulary={vocabulary}
                live={live}
                onRestore={restoredState => update(() => restoredState)}
                onOpen={openJob}
              />
            ) : null}
            {showSeed ? (
              <Seed
                state={state}
                threads={threads}
                onSave={(seed: StoredSeed) => update(current => ({ ...current, seed }))}
                onForget={() => update(current => ({ ...current, seed: null }))}
              />
            ) : null}
            <LadderEvidence evidence={ladder.evidence} />
            {!has(ladder, 5) ? (
              <div className="row row--end">
                <button
                  type="button"
                  className="button button--small"
                  onClick={() => update(current => ({
                    ...current,
                    operatorRequested: [...new Set([...current.operatorRequested, 5 as const])]
                  }))}
                >
                  We already run our own machine
                </button>
              </div>
            ) : null}
          </div>
        );
      default:
        return (
          <Compose
            state={state}
            vocabulary={vocabulary}
            ladder={ladder}
            dispatcher={dispatcher}
            onPairPhone={() => setFocus({ kind: "pair", forOfferId: null })}
            onSent={job => {
              update(current => ({ ...current, jobs: [job, ...current.jobs] }));
              setJustSent(job.id);
              setFocus({ kind: "sent", job });
            }}
          />
        );
    }
  })();

  return (
    <Shell
      theme={theme}
      setTheme={setTheme}
      profile={state.profile}
      onProfile={profile => update(current => ({ ...current, profile }))}
      nav={
        <Nav
          view={view}
          onView={(next: View) => { setView(next); setFocus({ kind: "compose" }); setJustSent(null); }}
          counts={{
            out: todaysJobs.filter(job => job.finishedUnixSeconds == null).length,
            trouble: todaysJobs.filter(job => {
              const snapshot = live.get(job.id);
              return snapshot?.status.outcomes.some(
                outcome => outcome === 2 || outcome === 3
              ) ?? false;
            }).length,
            history: state.jobs.length,
            people: state.devices.filter(device => device.retiredUnixSeconds == null).length
          }}
          showPeople={showRoster}
          vocabulary={vocabulary}
        />
      }
    >
      {clockShiftMillis !== 0 ? (
        <div className="banner" role="note">
          <span className="banner__mark">Clock</span>
          <p className="banner__text">
            This console thinks today is{" "}
            <b>{new Date(nowMillis()).toLocaleDateString([], { weekday: "long", day: "numeric", month: "long" })}</b>.
            Everything sent from here is stamped with that date, which is how a fortnight of history
            can be walked through in an afternoon. Drop <code className="mono">?today=</code> from
            the address to go back to the real one.
          </p>
        </div>
      ) : null}

      {!writable.current ? (
        <div className="banner banner--halt" role="alert">
          <span className="banner__mark">Note</span>
          <p className="banner__text">
            This browser will not let the page keep anything. What you send will still work — the
            links are real — but this console will forget it the moment you reload.
          </p>
        </div>
      ) : null}

      {openError ? (
        <div className="banner banner--halt" role="alert">
          <span className="banner__mark">Broken</span>
          <p className="banner__text">{openError}</p>
        </div>
      ) : null}

      {session && sessionState?.map.kind === "none" ? (
        <Panel eyebrow="No routing map here">
          <Note title="Nothing can be sent from this console." tone="halt">
            {sessionState.map.reason === "unreachable"
              ? "A map was configured and could not be read."
              : "Route graphs are published region by region and this console has not been given one."}{" "}
            A job is bound to the map’s own fingerprint, and the order a round gets is worked out on
            it, so without one there is genuinely nothing to seal. This is what it looks like in a
            region nobody has published a graph for, and it is not pretending otherwise.
          </Note>
        </Panel>
      ) : null}

      {!session && !openError ? (
        <Panel>
          <p className="row" style={{ margin: 0, color: "var(--ink-faint)" }}>
            <span className="spinner" aria-hidden="true" />
            Opening the map…
          </p>
        </Panel>
      ) : null}

      {ladder.offer ? (
        <OfferBanner
          offer={ladder.offer}
          onAccept={() => onOffer(ladder.offer!, true)}
          onDismiss={() => onOffer(ladder.offer!, false)}
        />
      ) : null}

      {/*
        The composer is a form and a form 1100px wide is a worse form, so
        it keeps the narrow measure it has always had. The other rooms are
        a screen: a map, a day's rounds, a record. Capping those at reading
        width is what made this app feel like a page rather than a place.
      */}
      <div className={`columns${rail && view === "send" ? " columns--split" : ""}${
        view === "send" ? "" : " columns--wide"
      }`}>
        <div className="stack">{middle}</div>

        {/*
          The rail is the composer's companion and belongs to that room
          only: rung-earned tools beside the thing being composed. The
          other rooms are whole screens, and a board squeezed next to a
          map is neither.
        */}
        {rail && view === "send" ? (
          <div className="stack">
            {showBoard ? (
              <DayBoard
                state={state}
                vocabulary={vocabulary}
                live={live}
                onHandOver={job => setFocus({ kind: "handover", job })}
                onOpen={openJob}
                onFinish={finishJob}
              />
            ) : null}

            {showRoutes ? (
              <Routes
                state={state}
                vocabulary={vocabulary}
                dispatcher={dispatcher}
                onSave={(route: StoredRoute) => update(current => ({
                  ...current,
                  routes: [route, ...current.routes.filter(entry => entry.id !== route.id)]
                }))}
                onDispatchDay={(_route: StoredRoute, _day: StoredRouteDay, job: StoredJob) => {
                  update(current => ({ ...current, jobs: [job, ...current.jobs] }));
                  setJustSent(job.id);
                  setFocus({ kind: "sent", job });
                }}
              />
            ) : null}

            {showOffers ? (
              <Offers
                state={state}
                vocabulary={vocabulary}
                dispatcher={dispatcher}
                onSaveOffer={(offer: StoredOffer) => update(current => ({
                  ...current,
                  offers: [offer, ...current.offers.filter(entry => entry.id !== offer.id)]
                }))}
                onCollectBid={(offer: StoredOffer) => setFocus({ kind: "pair", forOfferId: offer.id })}
                onAward={(deviceId, job, driverUrl, sealedB64) => {
                  update(current => ({
                    ...current,
                    jobs: current.jobs.map(entry => (entry.id === job.id
                      ? {
                        ...entry,
                        deviceId,
                        heldBy: deviceId,
                        driverName: current.devices.find(d => d.id === deviceId)?.name ?? null,
                        driverUrl,
                        sealedB64,
                        usedRungs: entry.usedRungs.includes(4) ? entry.usedRungs : [...entry.usedRungs, 4]
                      }
                      : entry))
                  }));
                }}
              />
            ) : null}
          </div>
        ) : null}
      </div>

      <footer className="footer">
        <p>
          Everything on this screen was made on this machine. The addresses came out of a map index
          read by byte range, the jobs were sealed here, and no server was told any of it — there is
          no server. What the {vocabulary.jobs} are doing is read off the mesh from the runs’ own
          signed records, on the same links your {vocabulary.recipient}s hold.
        </p>
      </footer>
    </Shell>
  );
}

function Shell({
  theme,
  setTheme,
  profile,
  onProfile,
  nav,
  children
}: {
  theme: Theme;
  setTheme(theme: Theme): void;
  profile: ProfileId | null;
  onProfile(profile: ProfileId): void;
  /** Navigation lives in the rail, for good. */
  nav?: React.ReactNode;
  children: React.ReactNode;
}): JSX.Element {
  /*
    An application frame, not a stack of cards on a page.

    This used to be a single centred column with every surface floating
    in it as its own rounded, shadowed box — nav in a box, banners in
    boxes, the map in a box inside a box — which is why a dispatcher's
    monitor showed a lot of beige and a little console. Now the window
    *is* the console: a rail that holds navigation for good, a bar that
    holds the things that are true of the whole app, and one view region
    that fills whatever is left and scrolls by itself.
  */
  return (
    <div className="shell">
      <aside className="rail" aria-label="Console">
        <span className="rail__mark">
          <img src={markUrl} alt="" aria-hidden="true" />
        </span>
        {nav}
        <div className="rail__foot">
          <button
            type="button"
            className="rail__tool"
            onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
            aria-label={`Switch to ${theme === "dark" ? "day" : "night"} colours`}
          >
            {theme === "dark" ? "Day" : "Night"}
          </button>
        </div>
      </aside>

      <div className="frame">
        <header className="frame__bar">
          <span className="wordmark">
            <span className="wf-wordmark">Wayfind</span>
          </span>
          <div className="frame__tools">
            {profile ? <ProfileSwitch profile={profile} onPick={onProfile} /> : null}
          </div>
        </header>
        <main className="frame__view">{children}</main>
      </div>
    </div>
  );
}

/** §20.5 — the same job, handed to a different phone. */
function HandOverTo({
  job,
  state,
  dispatcher,
  onCancel,
  onDone
}: {
  job: StoredJob;
  state: FleetState;
  dispatcher: Dispatcher | null;
  onCancel(): void;
  onDone(job: StoredJob, deviceId: string, driverUrl: string, sealedB64: string): void;
}): JSX.Element {
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const devices = state.devices.filter(
    device => device.retiredUnixSeconds == null && device.id !== job.deviceId
  );

  const give = useCallback(async (deviceId: string) => {
    if (!dispatcher) return;
    setBusy(true);
    setProblem(null);
    try {
      const device = state.devices.find(entry => entry.id === deviceId);
      if (!device) throw new Error("That phone is not paired.");
      const resealed = await dispatcher.reseal({
        sealedB64: job.sealedB64,
        consolePrivateKeyHex: state.consoleDevice.privateKeyHex,
        recipients: [device.publicKeyHex, state.consoleDevice.publicKeyHex]
      });
      onDone(job, deviceId, resealed.driverUrl, resealed.sealedB64);
    } catch (error) {
      setProblem(error instanceof Error ? error.message : String(error));
    } finally {
      setBusy(false);
    }
  }, [dispatcher, job, state.devices, state.consoleDevice.privateKeyHex, state.consoleDevice.publicKeyHex, onDone]);

  return (
    <Panel
      eyebrow="Hand it to somebody else"
      aside={<button type="button" className="button button--small" onClick={onCancel}>Cancel</button>}
    >
      <Note title="The same job, not a new one." tone="quiet">
        The link you already gave out keeps working. It is made from a key inside these bytes, so
        reissuing would break every link already sent — which is the whole reason a dispatcher holds
        the job rather than the driver minting it.
      </Note>
      <div className="rows" style={{ marginTop: "0.7rem" }}>
        {devices.map(device => (
          <div key={device.id} className="rowcard">
            <div className="rowcard__main">
              <span className="rowcard__name">{device.name}</span>
              <span className="rowcard__sub">Paired</span>
            </div>
            <code className="fingerprint wf-code">{device.fingerprint}</code>
            <button
              type="button"
              className="button button--small"
              disabled={busy}
              onClick={() => { void give(device.id); }}
            >
              Give it to them
            </button>
          </div>
        ))}
        {!devices.length ? <p className="small muted">No other phone is paired.</p> : null}
      </div>
      <hr className="divider" />
      <Copyable label="What is being handed over" value={job.driverUrl} />
      <p className="field__hint" style={{ marginTop: "0.4rem" }}>
        Stopping the first phone is your job, not the protocol’s. Two phones holding one job are
        indistinguishable from each other by construction — say so to whoever had it.
      </p>
      {problem ? (
        <div className="banner banner--halt" role="alert" style={{ marginTop: "0.6rem" }}>
          <span className="banner__mark">No</span>
          <p className="banner__text">{problem}</p>
        </div>
      ) : null}
    </Panel>
  );
}

/**
 * The rooms of the console.
 *
 * Four questions a dispatcher has, in the order the day asks them: what
 * am I sending, what is happening, what happened, and who is driving.
 * The ladder still governs what *exists* — People is absent until a
 * second phone makes a roster mean something — but going and looking at
 * the day was never a rung concept, and hiding it behind the composer is
 * what made this console feel like a form rather than a place.
 *
 * Counts are on the tabs because a dispatcher's eye should be pulled by
 * the thing that needs them, and never anywhere else: only two numbers
 * appear, what is out and what is wrong, and the second one is the only
 * thing in this bar that is allowed to be clay.
 */
function Nav({
  view,
  onView,
  counts,
  showPeople,
  vocabulary
}: {
  view: View;
  onView(view: View): void;
  counts: { out: number; trouble: number; history: number; people: number };
  showPeople: boolean;
  vocabulary: Vocabulary;
}): JSX.Element {
  const tabs: { id: View; label: string; badge?: number; tone?: "halt" }[] = [
    { id: "send", label: "Send" },
    { id: "live", label: "Live", badge: counts.out || undefined },
    { id: "history", label: "History", badge: counts.history || undefined },
    ...(showPeople
      ? [{ id: "people" as View, label: capitalise(vocabulary.driver) + "s", badge: counts.people || undefined }]
      : []),
    { id: "settings", label: "Settings" }
  ];

  /*
    The count sits above the word, not beside it.

    In the rail there is room for exactly one number per section, and a
    dispatcher's eye should land on it before it lands on the label —
    "3" then "live", not "Live (3)". A section with nothing to count
    shows no number at all; the tab holds its height from CSS instead, so
    the labels stay a column and a section gaining its first job does not
    shove the rail around.
  */
  return (
    <nav className="nav" aria-label="Console sections">
      {tabs.map(tab => (
        <button
          key={tab.id}
          type="button"
          className={`nav__tab${view === tab.id ? " nav__tab--on" : ""}${
            tab.id === "live" && counts.trouble ? " nav__tab--halt" : ""
          }`}
          aria-current={view === tab.id ? "page" : undefined}
          onClick={() => onView(tab.id)}
        >
          {tab.badge ? (
            <span className="nav__count wf-numeric">{tab.badge}</span>
          ) : null}
          <span className="nav__label">{tab.label}</span>
          {tab.id === "live" && counts.trouble ? (
            <span className="nav__flag" title="A stop was not delivered">
              {counts.trouble} not delivered
            </span>
          ) : null}
        </button>
      ))}
    </nav>
  );
}

function capitalise(word: string): string {
  return word.charAt(0).toUpperCase() + word.slice(1);
}
